import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Loads data on mount and exposes a reload.
 *
 * Results from a superseded request are discarded. Round trips to Paris are
 * ~70 ms and reloads are user-triggered, so responses can and do arrive out of
 * order; without the guard a slow earlier request would overwrite fresher data.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const requestId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // `fn` is typically an inline closure, so depending on it directly would
  // re-run every render. The caller's `deps` describe when it truly changes.
  const run = useCallback(fn, deps);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    void run().then(
      (value) => {
        if (!mounted.current || id !== requestId.current) return;
        setData(value);
        setLoading(false);
      },
      (err: unknown) => {
        if (!mounted.current || id !== requestId.current) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
  }, [run, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}
